/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { Button } from '@mui/material';
import { SubTitleLayout, SubTitleGroup } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { useTranslation } from 'react-i18next';
import CustomDialog from 'components/modals/common/CustomDialog';
import CustomGrid from 'components/grids/CustomGrid';
import { useMessageBar } from 'hooks/useMessageBar';
import { DrwList, SearchParam } from 'models/mp/MpDrw';
import { getDrwList } from 'apis/mp/MpDrw';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import { MultiComboBox } from 'components/selects/ComboBox';
import { useCommonModal } from 'hooks/useCommonModal';

/**
 * @description 제작사양서 선택 팝업
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 *  25.11.12    jihwan89       설비변경점 개선사항 v5.2 제작사양서 해당사항없음 선태 시 FDM 연계없이 완료 처리
 */

interface Props {
  open: boolean;
  close: (isDone?: boolean) => void;
  condition: any;
  selectedIds?: string[]; // 선택된 도면ID (drwId)
  onCallbackData: any;
}

export const MpDrwPopUp = ({ open, close, condition, onCallbackData, selectedIds = [] }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();

  const gridRef = useRef<any>();
  const [flexRef, setFlexRef] = useState<any>();
  const [rowData, setRowData] = useState<DrwList[]>([]);

  const [searchState, setSearchState] = useState<boolean>(false);

  const [checkedItems, setCheckedItems] = useState<DrwList[]>([]);

  const [searchParam, setSearchParam] = useState<SearchParam>({
    drwNo: '',
    drwNm: '',
  });

  useEffect(() => {
    if (!_.isEmpty(condition)) {
      setSearchState(true);
    }
  }, [condition]);

  useEffect(() => {
    selectDrwList(searchParam);
  }, [searchState]);

  useEffect(() => {
    checkedDefaultIds();
  }, [rowData]);

  const checkedDefaultIds = () => {
    (gridRef.current?.rows || []).map((o) => {
      o.isSelected = (selectedIds || []).includes(o.dataItem.drwId);
    });
    gridRef.current?.refresh();
  };

  const handleOnChange = (name, value) => {
    setSearchParam({ ...searchParam, [name]: value });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      btnSearch();
    }
  };

  const btnSearch = () => {
    selectDrwList(searchParam);
  };

  const selectDrwList = (searchParam) => {
    getDrwList(searchParam).then((result) => {
      if (result !== null) {
        setRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
        return false;
      }
    });
  };

  const layoutDefinition = [
    {
      binding: 'drwId',
      visible: false,
    },
    {
      binding: 'drwNo',
      header: String(t('mp.label.관리번호', '관리번호')),
      align: 'center',
      width: 200,
    },
    {
      binding: 'drwTpCd',
      header: String(t('mp.label.도면유형코드', '도면유형코드')),
      align: 'center',
      isReadOnly: true,
      visible: false,
    },
    {
      binding: 'drwTpNm',
      header: String(t('mp.label.유형명', '유형명')),
      align: 'center',
      width: 150,
    },
    {
      binding: 'drwNm',
      header: String(t('mp.label.자료명', '자료명')),
      align: 'left',
      width: '*',
    },
  ];

  const onInitialized = (grid) => {
    setFlexRef(grid);
    gridRef.current = grid;
    checkedDefaultIds();
  };

  const handleConfirm = () => {
    // 25.11.12 설비변경점 개선사항 v5.2 제작사양서 해당사항없음 선태 시 FDM 연계없이 완료 처리
    // drwId === '1' 은 제작사양서 해당사항없음
    // 제작사양서 여러개 선택시에도 해당사항없음이 포함되면 확인알람 띄운 후 해당사항없음 데이터만 제공
    const selectedCpltDrw = checkedItems.filter((item) => item.drwId === '1');
    if (selectedCpltDrw.length > 0) {
      openCommonModal({
        modalType: 'confirm',
        content: t(
          `com.msg.해당사항없음을 선택하셨습니다. FDM과 연계되지 않고 제작사양서가 완료 처리됩니다. 진행하시겠습니까?`,
          `해당사항없음을 선택하셨습니다. FDM과 연계되지 않고 제작사양서가 완료 처리됩니다. 진행하시겠습니까?`
        ),
        yesCallback: () => {
          onCallbackData(selectedCpltDrw);
          handleClose();
          return true;
        },
        noCallback: () => {
          return false;
        },
      });
    } else {
      onCallbackData(checkedItems);
      handleClose();
    }
  };

  const btnReload = () => {
    setSearchParam(
      (prev) =>
        Object.keys(prev).reduce((acc, cur) => Object.assign(acc, { [cur]: '' }), {}) as SearchParam
    );
  };

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={handleConfirm}
      disableRipple
    >
      {t('com.button.선택', '선택')}
    </Button>,
  ];

  const handleClose = () => {
    close();
  };

  return (
    <>
      <CustomDialog
        open={open}
        title={t('mp.label.제작사양서 선택', '제작사양서 선택')}
        size="md"
        onClose={handleClose}
        onCancel={handleClose}
        buttons={dialogButtons}
      >
        <SearchBox>
          <SearchBoxRow>
            <InputBox>
              <SearchRows className="twoCol">
                <SearchCols>
                  <label>{t('mp.label.관리번호', '관리번호')}</label>
                  <CustomInputWithSearch
                    type="text"
                    name="drwNo"
                    placeholder={String(
                      t('mp.label.관리번호를 입력해 주세요.', '관리번호를 입력해 주세요.')
                    )}
                    value={searchParam?.drwNo}
                    onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                    onKeyDown={onKeyDown}
                  />
                </SearchCols>
                <SearchCols>
                  <label>{t('mp.label.자료명', '자료명')}</label>
                  <CustomInputWithSearch
                    type="text"
                    name="drwNm"
                    placeholder={String(
                      t('ut.label.자료명을 입력해 주세요.', '자료명을 입력해 주세요.')
                    )}
                    value={searchParam?.drwNm}
                    onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                    onKeyDown={onKeyDown}
                  />
                </SearchCols>
              </SearchRows>
            </InputBox>
            <SearchButtonWrap>
              <Button
                css={IconButton.button}
                className="reload"
                onClick={btnReload}
                disableRipple
              ></Button>
              <Button css={IconButton.button} className="find" onClick={btnSearch} disableRipple>
                {t('com.button.조회', '조회')}
              </Button>
            </SearchButtonWrap>
          </SearchBoxRow>
        </SearchBox>
        <SubTitleLayout>
          <SubTitleGroup>
            <h3>{t('mp.label.제작사양서 List', '제작사양서 List')}</h3>
            <span className="total">
              {t('com.label.총', '총')}
              <span>{(rowData || []).length.toLocaleString()}</span>
              {t('com.label.건', '건')}
            </span>
          </SubTitleGroup>
        </SubTitleLayout>
        <CustomGrid
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          allowPinning={false}
          height={300}
          align={'center'}
          autoCheck={true}
          isReadOnly={true}
          rowSelection="multiple"
          initialized={onInitialized}
          loadedRows={(grid, e) => {
            console.log(grid.rows, selectedIds);
            grid.rows.forEach((o) => {
              if ((selectedIds || []).includes(o.dataItem.drwId)) {
                o.isSelected = true;
              }
            });
            grid.invalidate();
          }}
          onChangeCheckedItem={(items) => setCheckedItems(items)}
        />
      </CustomDialog>
    </>
  );
};
